import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import cx from "classnames";
import { Link } from "react-router-dom";
import "./Table.css";
import {
  formatDate,
  getDayOfMonth,
  isWeekend,
  isInPast,
  isWeekAfter,
  isSunday,
} from "../../inc/helpers/date";

function ApprovalCheckbox({ day, selectedMonth, getHours, member }) {
  const [daysInWeek, setDaysInWeek] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [isAllHoursLogged, setIsAllHoursLogged] = useState(false);

  useEffect(() => {
    // Returns array of dates Mon - Sun, including days from previous month if the week straddles 2 months
    const newDaysInWeek = Array.from(Array(7), (_v, i) =>
      day - i > 0
        ? getDayOfMonth(day - i, selectedMonth)
        : moment(selectedMonth)
            .subtract(Math.abs(day - i - 1), "d")
            .format("YYYY-MM-DD")
    ).sort((a, b) => new Date(a) - new Date(b));

    setDaysInWeek(newDaysInWeek);
  }, [day, selectedMonth]);

  useEffect(() => {
    if (!daysInWeek) return;

    const isWeekApproved = daysInWeek.every(
      (day) =>
        member.approved.findIndex(
          (approvedDay) =>
            formatDate(approvedDay.date) === day
        ) > -1
    );

    setIsApproved(isWeekApproved);
  }, [daysInWeek, member.id, member.approved]);

  useEffect(() => {
    if (!daysInWeek) return;
    const newIsAllHoursLogged = daysInWeek.every(
      (dayOfWeek) =>
        isWeekend(dayOfWeek) ||
        getHours(dayOfWeek, member) >= member.timesheetsRequiredDailyHours
    );

    setIsAllHoursLogged(newIsAllHoursLogged);
  }, [daysInWeek, member, getHours]);

  const toggleApproved = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/api/approve`,
        {
          headers: {
            "x-access-token": localStorage.getItem("access_token"),
            "content-type": "application/json",
          },
          method: isApproved ? "DELETE" : "POST",
          body: JSON.stringify({
            week: daysInWeek,
            approvedFor: member.id,
          }),
        }
      );

      if (response.ok) {
        setIsApproved(!isApproved);
      }
    } catch (e) {
      console.log(e.message);
    }
  };

  return (
    <td
      data-testid="approval-cell"
      className={`Table__Cell Table__Cell--Approved ${cx({
        "Table__Cell--Success": isApproved,
        "Table__Cell--Danger":
          daysInWeek &&
          !isApproved &&
          isWeekAfter(daysInWeek[daysInWeek.length - 1]),
      })}`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        disabled={!isAllHoursLogged && !isApproved}
        title={
          (isApproved && "Unapprove Day") ||
          (isAllHoursLogged && "Approve Day") ||
          "Unable to approve day. Not all hours logged."
        }
        type="checkbox"
        checked={isApproved}
        value={isApproved}
        onChange={toggleApproved}
      />
    </td>
  );
}

function Table({ team, selectedMonth, }) {
  const [tableScrollX, setTableScrollX] = useState(0);
  const [clientX, setClientX] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const ref = useRef(null);

  const onMouseDown = (e) => {
    setIsScrolling(true);
    setClientX(e.clientX);
  };

  const onMouseUp = () => {
    setIsScrolling(false);
  };

  const onMouseMove = (e) => {
    if (!isScrolling || !ref.current) return;

    e.preventDefault();

    const newScrollPos = tableScrollX - e.clientX + clientX;
    setTableScrollX(newScrollPos);
    setClientX(e.clientX);
    ref.current.scrollLeft = tableScrollX;
  };

  const daysInMonth = Array.from(
    Array(moment(selectedMonth).daysInMonth()),
    (_v, i) => i + 1
  );

  const getHours = (date, member) => member.time[date] / 60 || 0;

  const isAllTeamHoursLogged = (date) =>
    team &&
    (team.every(
      (member) => getHours(date, member) >= member.timesheetsRequiredDailyHours
    ) ||
      isWeekend(getDayOfMonth(date)));

  const isAnyTeamHoursLogged = (date) =>
    team && team.some((member) => getHours(date, member) > 0);

  const formatOrdinal = (day) => {
    const suffixes = {
      one: "st",
      two: "nd",
      few: "rd",
      other: "th",
    };

    const pluralRules = new Intl.PluralRules("en", { type: "ordinal" });

    const rule = pluralRules.select(day);

    return `${day}${suffixes[rule]}`;
  };

  return (
    <table className="Table">
      <tbody
        ref={ref}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        className={`Table__Body ${isScrolling ? "Table__Body--Scrolling" : ""}`}
      >
        <tr className="Table__Row" data-testid="table-head">
          <td className="Table__Cell Table__Cell--Sticky" />
          {daysInMonth
            ? daysInMonth.map((day) => {
                const date = getDayOfMonth(day, selectedMonth);

                return (
                  <React.Fragment key={`thead-${day}`}>
                    <td
                      data-testid="day-cell"
                      className={`Table__Cell ${cx({
                        "Table__Cell--Success":
                          isInPast(date) &&
                          !isWeekend(date) &&
                          isAllTeamHoursLogged(date),
                        "Table__Cell--Danger":
                          isInPast(date) &&
                          !isWeekend(date) &&
                          !isAllTeamHoursLogged(date),
                        "Table__Cell--WeekendTime":
                          isInPast(date) &&
                          isWeekend(date) &&
                          isAnyTeamHoursLogged(date),
                      })}`}
                    >
                      {formatOrdinal(day)}
                    </td>
                    {isSunday(date) && (
                      <td className="Table__Cell--Approved">Approved</td>
                    )}
                  </React.Fragment>
                );
              })
            : null}
        </tr>
        {team &&
          team
            .sort((a, b) => a.name.localeCompare(b.name, "en"))
            .map((member) => (
              <React.Fragment key={member.id}>
                <tr className="Table__Row" data-testid={`${member.id}-row`}>
                  <td className="Table__Cell Table__Cell--Sticky">
                    {member.name}
                  </td>
                  {daysInMonth &&
                    daysInMonth.map((day) => {
                      const date = getDayOfMonth(day, selectedMonth);
                      const hours = getHours(date, member);
                      return (
                        <React.Fragment key={`${member.id}-${day}`}>
                          <td
                            data-testid="hours-cell"
                            className={`Table__Cell ${cx({
                              "Table__Cell--Success":
                                isInPast(date) &&
                                !isWeekend(date) &&
                                hours >= member.timesheetsRequiredDailyHours,
                              "Table__Cell--Danger":
                                isInPast(date) &&
                                !isWeekend(date) &&
                                hours < member.timesheetsRequiredDailyHours,
                              "Table__Cell--WeekendTime":
                                isWeekend(date) && hours > 0,
                            })}
                        `}
                          >
                            <Link
                              data-testid="link"
                              to={`/users/${
                                member.id
                              }?name=${encodeURIComponent(
                                member.name
                              )}&date=${date}&requiredMinutes=${
                                member.timesheetsRequiredDailyHours
                              }`}
                            >
                              {hours.toFixed(1)}
                            </Link>
                          </td>
                          {isSunday(date) && (
                            <ApprovalCheckbox
                              day={day}
                              selectedMonth={selectedMonth}
                              getHours={getHours}
                              member={member}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                </tr>
              </React.Fragment>
            ))}
      </tbody>
    </table>
  );
}

export default Table;
